import { useState } from 'react';
import { Text, Button, Spinner, IconButton } from 'frosted-ui';
import {
  Plus16,
  XMark16,
  CouponActive16,
  ArrowUpRightFromSquare16,
} from '@frosted-ui/icons';
import { api, WhopPermissionRequiredError } from '../../services/api';
import { formatPromoDiscount, formatPromoDuration } from '../../utils/promo';
import type { EligiblePromo } from '../../types';

export interface SelectedPromo {
  id: string;
  code: string;
  amountOff: number;
  promoType: 'percentage' | 'flat_amount';
  duration: 'forever' | 'once' | 'repeating' | null;
  promoDurationMonths: number | null;
}

interface VariantPromoPickerProps {
  companyId: string;
  productId: string;
  selectedPromoId?: string;
  selectedPromoCode?: string;
  onChange: (promo: SelectedPromo | null) => void;
}

export default function VariantPromoPicker({
  companyId,
  productId,
  selectedPromoId,
  selectedPromoCode,
  onChange,
}: VariantPromoPickerProps) {
  const [expanded, setExpanded] = useState(false);
  const [promos, setPromos] = useState<EligiblePromo[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [permissionRequired, setPermissionRequired] = useState(false);

  async function handleExpand() {
    setExpanded(true);
    // Cache the result across open/close cycles within the same wizard session.
    if (promos !== null || loading) return;

    setLoading(true);
    setError(null);
    setPermissionRequired(false);
    try {
      const data = await api.listEligiblePromos(companyId, productId);
      setPromos(data);
    } catch (err) {
      // Creators who installed the app before promo permissions were added
      // hit this on the first call. Surface a re-approve prompt rather than
      // a generic error message.
      if (err instanceof WhopPermissionRequiredError) {
        setPermissionRequired(true);
      } else {
        setError((err as Error).message || 'Failed to load promos');
      }
    } finally {
      setLoading(false);
    }
  }

  // Selected state — show even when collapsed, since the promo *is* part of
  // the variant's identity at that point.
  if (selectedPromoId && selectedPromoCode) {
    return (
      <div
        style={{
          marginTop: 'var(--space-4)',
          padding: 'var(--space-3)',
          borderRadius: 'var(--radius-2)',
          border: '1px solid var(--accent-a6)',
          backgroundColor: 'var(--accent-a3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
          <CouponActive16 style={{ color: 'var(--accent-11)', flexShrink: 0 }} />
          <Text size="2" weight="medium" style={{ fontFamily: 'monospace' }}>
            {selectedPromoCode}
          </Text>
        </div>
        <IconButton
          variant="ghost"
          color="gray"
          size="1"
          onClick={() => {
            onChange(null);
            setExpanded(false);
          }}
          aria-label="Remove promo code"
          title="Remove promo code"
        >
          <XMark16 />
        </IconButton>
      </div>
    );
  }

  if (!expanded) {
    return (
      <Button
        variant="ghost"
        color="gray"
        size="2"
        onClick={handleExpand}
        style={{ marginTop: 'var(--space-3)', alignSelf: 'flex-start' }}
      >
        <Plus16 />
        Add promo code
      </Button>
    );
  }

  return (
    <div style={{ marginTop: 'var(--space-4)' }}>
      <div
        style={{
          padding: 'var(--space-3)',
          border: '1px solid var(--gray-a6)',
          borderRadius: 'var(--radius-2)',
          backgroundColor: 'var(--gray-a2)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--space-2)',
          }}
        >
          <Text size="2" weight="medium">
            Choose a promo code
          </Text>
          <IconButton
            variant="ghost"
            color="gray"
            size="1"
            onClick={() => setExpanded(false)}
            aria-label="Cancel"
          >
            <XMark16 />
          </IconButton>
        </div>

        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-4)' }}>
            <Spinner size="2" />
          </div>
        )}

        {!loading && permissionRequired && (
          <div
            style={{
              padding: 'var(--space-3)',
              borderRadius: 'var(--radius-2)',
              backgroundColor: 'var(--yellow-a3)',
              border: '1px solid var(--yellow-a6)',
            }}
          >
            <Text size="2" weight="medium" color="yellow" style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
              One more permission needed
            </Text>
            <Text size="2" color="yellow" style={{ display: 'block', marginBottom: 'var(--space-3)' }}>
              This feature needs one additional Whop permission to access promo codes. Open your authorized apps, find the "Updates requested" alert, re-approve the app, then try again.
            </Text>
            <a
              href={`https://whop.com/dashboard/${companyId}/settings/authorized-apps`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                color: 'var(--yellow-11)',
                fontSize: 'var(--font-size-2)',
                fontWeight: 500,
                textDecoration: 'none',
              }}
            >
              Open authorized apps
              <ArrowUpRightFromSquare16 />
            </a>
          </div>
        )}

        {!loading && !permissionRequired && error && (
          <Text size="2" color="red" style={{ display: 'block', padding: 'var(--space-2)' }}>
            {error}
          </Text>
        )}

        {!loading && !permissionRequired && !error && promos && promos.length === 0 && (
          <Text size="2" color="gray" style={{ display: 'block', padding: 'var(--space-2)' }}>
            No eligible promo codes found for this product.
          </Text>
        )}

        {!loading && !permissionRequired && !error && promos && promos.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', maxHeight: 240, overflowY: 'auto' }}>
            {promos.map((promo) => {
              const durationLabel = formatPromoDuration(promo.duration, promo.promoDurationMonths);
              return (
                <button
                  key={promo.id}
                  onClick={() => {
                    onChange({
                      id: promo.id,
                      code: promo.code,
                      amountOff: promo.amountOff,
                      promoType: promo.promoType,
                      duration: promo.duration,
                      promoDurationMonths: promo.promoDurationMonths,
                    });
                    setExpanded(false);
                  }}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    padding: 'var(--space-3)',
                    borderRadius: 'var(--radius-2)',
                    border: '1px solid var(--gray-a6)',
                    backgroundColor: 'var(--color-panel-solid)',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0 }}>
                    <CouponActive16 style={{ color: 'var(--accent-11)', flexShrink: 0 }} />
                    <Text size="2" weight="medium" style={{ fontFamily: 'monospace' }}>
                      {promo.code}
                    </Text>
                  </div>
                  <Text size="2" color="gray" style={{ flexShrink: 0 }}>
                    {formatPromoDiscount(promo.amountOff, promo.promoType, promo.currency)}
                    {durationLabel ? ` • ${durationLabel}` : ''}
                  </Text>
                </button>
              );
            })}
          </div>
        )}

        <div
          style={{
            marginTop: 'var(--space-3)',
            paddingTop: 'var(--space-3)',
            borderTop: '1px solid var(--gray-a6)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-2)',
          }}
        >
          <Text size="1" color="gray">
            Promo not applying at checkout? Check for plan-specific restrictions in your Whop dashboard.
          </Text>
          <a
            href={`https://whop.com/dashboard/${companyId}/promo`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
              color: 'var(--accent-11)',
              fontSize: 'var(--font-size-2)',
              textDecoration: 'none',
            }}
          >
            Don't see your code? Create one in your Whop dashboard
            <ArrowUpRightFromSquare16 />
          </a>
        </div>
      </div>
    </div>
  );
}
